import { css } from '@emotion/react'
import styled from '@emotion/styled'
import { mdiWebcam, mdiWebcamOff } from '@mdi/js'
import Icon from '@mdi/react'
import { Fade, Tooltip } from '@mui/material'
import { memo, useCallback } from 'react'
import { openMyWebcamFloater } from '../../app-state/modals/state.floater'
import { AppStateType } from '../../constants/enums'
import { BROADCASTING_STATE } from '../../constants/myWebcam'
import i18n from '../../utilities/i18n'
import { useAppStateSelector } from '../Hooks/useAppState'
import { useUserSelector } from '../Hooks/useUser'
import Notification from '../presentation/Notification'
import APP_BREAKPOINTS, { minWindowWidth } from '../styles/breakpoints'
import { palette } from '../styles/colors'
import { pulse } from '../styles/keyframes'
import { PAPER_HEIGHTS } from '../theme'
import IconButton from './IconButton'

const titles = {
    OFF: i18n.t('userBroadcast.broadcastingOff'),
    ON: i18n.t('userBroadcast.broadcastingOn'),
    ERROR: i18n.t('userBroadcast.broadcastingError'),
    NOT_AVAILABLE: i18n.t('userBroadcast.broadcastingNotAvailable'),
    UNINITIALIZED: i18n.t('userBroadcast.uninitialized'),
}

const icons = {
    OFF: mdiWebcam,
    ON: mdiWebcam,
    ERROR: mdiWebcamOff,
    NOT_AVAILABLE: mdiWebcamOff,
    UNINITIALIZED: mdiWebcam,
}

const colors = {
    OFF: palette.contrastIcons,
    ON: palette.secondary,
    ERROR: palette.orangeMFC,
    NOT_AVAILABLE: palette.inactiveGrey,
}

const iconStyles = css({
    padding: 0,
    marginRight: 6,
    border: 0,
    '& svg': {
        height: '1.31251rem',
        width: '1.3125rem',
    },
    '@media only screen and (max-width: 410px)': {
        '& svg ': {
            height: '1.26rem',
            width: '1.26rem',
        },
    },
    '@media only screen and (max-width: 400px)': {
        '& svg ': {
            height: '1.23rem',
            width: '1.23rem',
        },
    },
    '@media only screen and (max-width: 390px)': {
        '& svg ': {
            height: '1.2rem',
            width: '1.2rem',
        },
    },
    [minWindowWidth(APP_BREAKPOINTS.md)]: {
        padding: 9.5,
        marginRight: 0,
    },
})

const BroadcastDot = styled.div({
    display: 'inline-block',
    width: 10,
    height: 10,
    marginRight: 8,
    borderRadius: '50%',
    backgroundColor: colors['ON'],
    animation: `${pulse} 2s ease-in-out infinite`,
})

interface IButtonUserBroadcast {
    uid: number
    compact?: boolean
    showParticipants?: boolean
}

const ButtonUserBroadcast = memo((props: IButtonUserBroadcast) => {
    const { uid, compact, showParticipants = true } = props

    const participants = useUserSelector(uid, 'participants') || []
    const broadcasting = useAppStateSelector(
        AppStateType.MYWEBCAM,
        'broadcasting',
    )
    const iconColor = colors[broadcasting]
    const iconPath = icons[broadcasting]
    const tooltipTitle = titles[broadcasting]
    const isBroadcastingAvailable =
        broadcasting !== BROADCASTING_STATE.NOT_AVAILABLE
    const numberOfParticipants = participants.length
    const counterParticipants = isBroadcastingAvailable &&
        showParticipants &&
        numberOfParticipants > 0 && (
            <Notification value={numberOfParticipants} small />
        )

    const handleClick = useCallback(() => {
        if (broadcasting !== BROADCASTING_STATE.NOT_AVAILABLE) {
            openMyWebcamFloater()
        }
    }, [broadcasting])

    if (compact) {
        if (broadcasting === 'ON') {
            return <BroadcastDot />
        }
        return null
    }

    return (
        <Tooltip
            disableFocusListener
            enterDelay={1000}
            enterTouchDelay={1500}
            leaveDelay={0}
            placement="top"
            title={tooltipTitle}
            slotProps={{
                popper: {
                    style: {
                        zIndex: PAPER_HEIGHTS.tooltip,
                    },
                },
            }}
            slots={{
                transition: Fade,
            }}
        >
            <IconButton
                data-testid="IconButton__UserBroadcastToggle"
                onClick={handleClick}
                css={iconStyles}
            >
                <Icon path={iconPath} color={iconColor} />
                {counterParticipants}
            </IconButton>
        </Tooltip>
    )
})

export default ButtonUserBroadcast
